import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../utils/apiService';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import DataLoadingState from './EMHAAssessment/DataLoadingState';
import ErrorState from './EMHAAssessment/ErrorState';
import NoQuestionsState from './EMHAAssessment/NoQuestionsState';
import GuidanceModal from './EMHAAssessment/GuidanceModal';
import FacilityInfoStep from './EMHAAssessment/FacilityInfoStep';
import QuestionsStep from './EMHAAssessment/QuestionsStep';
import ProcessingStep from './EMHAAssessment/ProcessingStep';

const EMHAAssessment = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [facilityInfo, setFacilityInfo] = useState({
    companyName: '',
    contactName: '',
    email: '',
    annualEnergySpend: '',
    majorIndustry: '',
    subIndustry: '',
    subIndustryId: null,
    industry: '',
    region: 'naics' // Default to NAICS region
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [showGuidanceModal, setShowGuidanceModal] = useState(true); // Show modal on initial load
  const [userHasChosen, setUserHasChosen] = useState(false); // Track if user made a choice
  const [requestingConsultant, setRequestingConsultant] = useState(false);

  const [emhaQuestions, setEmhaQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [subIndustriesMap, setSubIndustriesMap] = useState({});
  const [availableSubIndustries, setAvailableSubIndustries] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Auto-fill user information when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      logger.log('Auto-filling EMHA user information:', user);
      setFacilityInfo(prev => ({
        ...prev,
        email: user.email || prev.email,
        contactName: user.name || user.full_name || prev.contactName,
        companyName: user.company || user.organization || prev.companyName
      }));
    }
  }, [isAuthenticated, user]);

  // Fetch EMHA questions and industries on component mount
  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      setDataError(null);

      try {
        // Fetch both in parallel for faster loading
        const [questionsResponse, industriesResponse] = await Promise.all([
          apiClient.get('/emha/questions'),
          apiClient.get('/industries')
        ]);

        // Process questions
        if (questionsResponse.data.success) {
          setEmhaQuestions(questionsResponse.data.questions);
          setCategories(questionsResponse.data.categories);
          logger.log('✅ Loaded', questionsResponse.data.total_questions, 'EMHA questions');
        }

        // Process industries
        logger.log('EMHA Industries API response:', industriesResponse.data);
        
        const industriesData = industriesResponse.data.industries || industriesResponse.data.data || industriesResponse.data;
        logger.log('EMHA Raw industries data:', industriesData);
        logger.log(`EMHA Is array: ${Array.isArray(industriesData)}, Length: ${Array.isArray(industriesData) ? industriesData.length : 'N/A'}`);

        if (industriesData && Array.isArray(industriesData) && industriesData.length > 0) {
          // Show ALL industries (both NAICS and ANZSIC) - don't filter by region
          // Users can select from either classification system
          logger.log('Loading all EMHA industries (NAICS and ANZSIC) for dropdown');
          
          // Store full industry objects with region information
          // Group by name+region to allow same industry name in both NAICS and ANZSIC
          const uniqueIndustriesMap = new Map();
          industriesData.forEach(ind => {
            const name = ind.name || '';
            const region = ind.region || 'naics';
            // Use name+region as key to allow duplicates across regions
            const key = `${name}::${region}`;
            if (name && !uniqueIndustriesMap.has(key)) {
              uniqueIndustriesMap.set(key, ind);
            }
          });
          
          const uniqueIndustries = Array.from(uniqueIndustriesMap.values())
            .sort((a, b) => {
              // Sort by region first (NAICS before ANZSIC), then by name
              const regionA = (a.region || '').toLowerCase();
              const regionB = (b.region || '').toLowerCase();
              if (regionA !== regionB) {
                // NAICS comes before ANZSIC
                if (regionA === 'naics') return -1;
                if (regionB === 'naics') return 1;
                return regionA.localeCompare(regionB);
              }
              return (a.name || '').localeCompare(b.name || '');
            });
          
          logger.log(`EMHA Extracted ${uniqueIndustries.length} unique industries with regions:`, 
            uniqueIndustries.map(ind => `${ind.name} (${ind.region || 'N/A'})`));
          
          if (uniqueIndustries.length > 0) {
            setIndustries(uniqueIndustries);
            
            const subIndMap = {};
            uniqueIndustries.forEach(industry => {
              subIndMap[industry.name] = [];
            });
            setSubIndustriesMap(subIndMap);
            logger.log(`✅ Loaded ${uniqueIndustries.length} major industries for EMHA (both NAICS and ANZSIC)`);
          } else {
            logger.error('❌ No valid industry names found in EMHA response');
            setIndustries([]);
          }
        } else {
          logger.error('Invalid industries data format from API:', industriesData);
          logger.error('Response structure:', industriesResponse.data);
          setIndustries([]);
        }

      } catch (err) {
        logger.error('❌ Error fetching EMHA data:', err);
        setDataError('Failed to load assessment data. Please refresh the page.');
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, []); // Remove facilityInfo.region dependency to prevent reloading

  // Fetch sub-industries when major industry changes (NEW SCHEMA)
  useEffect(() => {
    const fetchSubIndustries = async () => {
      if (!facilityInfo.majorIndustry) {
        setAvailableSubIndustries([]);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        
        // Determine region from selected major industry
        // Find the industry object to get its region
        const selectedIndustry = industries.find(ind => {
          const name = ind.name || '';
          return name === facilityInfo.majorIndustry;
        });
        
        // Get region from selected industry, or default to facilityInfo.region, or 'naics'
        const industryRegion = selectedIndustry 
          ? (selectedIndustry.region || 'naics')
          : (facilityInfo.region || 'naics');
        
        logger.log(`Fetching EMHA sub-industries for ${facilityInfo.majorIndustry} (region: ${industryRegion})`);
        
        const response = await fetch(
          `${backendUrl}/api/sub-industries-by-major?major_industry=${encodeURIComponent(facilityInfo.majorIndustry)}&region=${industryRegion}`,
          { credentials: 'include' }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.sub_industries) {
          // Extract sub-industry names from the response
          const subIndustryNames = data.sub_industries.map(sub => sub.sub_industry || sub.name || '');
          setAvailableSubIndustries(subIndustryNames);
          logger.log(`✅ Loaded ${subIndustryNames.length} sub-industries for ${facilityInfo.majorIndustry} (EMHA, ${industryRegion})`);
        } else {
          // Fallback to legacy subIndustriesMap
          setAvailableSubIndustries(subIndustriesMap[facilityInfo.majorIndustry] || []);
          logger.log(`Using legacy sub-industries map for ${facilityInfo.majorIndustry}`);
        }
      } catch (error) {
        logger.error('Error fetching sub-industries (EMHA):', error);
        // Fallback to legacy subIndustriesMap
        setAvailableSubIndustries(subIndustriesMap[facilityInfo.majorIndustry] || []);
      }
    };

    fetchSubIndustries();
    
    // Clear sub-industry selection when major industry changes
    if (facilityInfo.subIndustry) {
      setFacilityInfo(prev => ({
        ...prev,
        subIndustry: '',
        subIndustryId: null,
        industry: ''
      }));
    }
  }, [facilityInfo.majorIndustry, industries, subIndustriesMap]);

  // Update sub_industry_id when sub-industry is selected (EMHA)
  useEffect(() => {
    if (facilityInfo.subIndustry && facilityInfo.majorIndustry) {
      // Try to find the sub-industry ID from the API response
      const fetchSubIndustryId = async () => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
          
          // Determine region from selected major industry
          const selectedIndustry = industries.find(ind => {
            const name = ind.name || '';
            return name === facilityInfo.majorIndustry;
          });
          
          // Get region from selected industry, or default to facilityInfo.region, or 'naics'
          const industryRegion = selectedIndustry 
            ? (selectedIndustry.region || 'naics')
            : (facilityInfo.region || 'naics');
          
          const response = await fetch(
            `${backendUrl}/api/sub-industries-by-major?major_industry=${encodeURIComponent(facilityInfo.majorIndustry)}&region=${industryRegion}`,
            { credentials: 'include' }
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.sub_industries) {
              const selectedSub = data.sub_industries.find(
                sub => (sub.sub_industry || sub.name) === facilityInfo.subIndustry
              );
              if (selectedSub?.id) {
                setFacilityInfo(prev => ({
                  ...prev,
                  subIndustryId: selectedSub.id,
                  industry: facilityInfo.subIndustry
                }));
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching sub-industry ID:', error);
        }
      };
      
      fetchSubIndustryId();
    }
  }, [facilityInfo.subIndustry, facilityInfo.majorIndustry, industries]);

  const handleRequestConsultantGuidance = async () => {
    setRequestingConsultant(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/live-session/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          assessment_type: 'EMHA'
        })
      });

      const data = await response.json();

      // Check for errors in response (even if status is 200)
      if (!response.ok || !data.success) {
        // Extract message from different possible error formats
        const errorMessage = data.message || data.detail || data.error || 'Failed to request consultant session';
        throw new Error(errorMessage);
      }

      if (data.success) {
        setUserHasChosen(true);
        // Navigate to dashboard where user can see pending session
        navigate('/dashboard', {
          state: {
            message: 'EMHA session request sent! Your consultant will be notified.',
            showPending: true
          }
        });
      }
    } catch (err) {
      logger.error('Error requesting consultant guidance:', err);
      const errorMessage = err.message || 'Failed to request consultant session. Please try again.';
      
      // Handle no consultant assigned error specially
      if (errorMessage.includes("don't have an assigned consultant") || errorMessage.includes("consultant assigned")) {
        toast.error(
          'No Consultant Assigned: A consultant has not been assigned to your account yet. Please contact your administrator or proceed with a self-guided assessment.',
          { duration: 6000 }
        );
      } else {
        toast.error(errorMessage, { duration: 5000 });
      }
      
      setShowGuidanceModal(false);
    } finally {
      setRequestingConsultant(false);
    }
  };

  const leapPhases = [
    { range: [0, 1], phase: 'Measure', color: '#ef4444' },
    { range: [1, 2], phase: 'Meter', color: '#f97316' },
    { range: [2, 3], phase: 'Monitor', color: '#eab308' },
    { range: [3, 4], phase: 'Manage', color: '#22c55e' },
    { range: [4, 5], phase: 'Maximise', color: '#10b981' }
  ];

  const handleFacilityInfoChange = (field) => (e) => {
    const value = e.target.value;

    setFacilityInfo(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      if (field === 'majorIndustry') {
        // Value format: "industryId::region" or "industryName::region"
        const [industryIdentifier, region] = value.includes('::') 
          ? value.split('::') 
          : [value, null];
        
        let selectedIndustry = null;
        let normalizedRegion = 'naics';
        
        if (region) {
          // Region is explicitly in the value
          normalizedRegion = region.toLowerCase();
          // Find industry by ID or name, BUT also match the region to avoid finding wrong industry
          selectedIndustry = industries.find(ind => {
            const id = ind.id || '';
            const name = ind.name || '';
            const indRegion = (ind.region || 'naics').toLowerCase();
            // Match both identifier AND region
            return (id === industryIdentifier || name === industryIdentifier) && indRegion === normalizedRegion;
          });
          
          // If not found with region match, try without region (fallback for edge cases)
          if (!selectedIndustry) {
            selectedIndustry = industries.find(ind => {
              const id = ind.id || '';
              const name = ind.name || '';
              return id === industryIdentifier || name === industryIdentifier;
            });
          }
        } else {
          // Fallback: find by name (legacy support)
          selectedIndustry = industries.find(ind => {
            const name = ind.name || '';
            return name === value;
          });
          
          if (selectedIndustry) {
            normalizedRegion = (selectedIndustry.region || 'naics').toLowerCase();
          }
        }
        
        // Extract industry name from selected industry
        const industryName = selectedIndustry
          ? selectedIndustry.name
          : (industryIdentifier.includes('::') ? industryIdentifier.split('::')[0] : industryIdentifier);
        
        updated.region = normalizedRegion;
        updated.majorIndustry = industryName; // Store just the name
        logger.log(`✅ Updated EMHA region to ${normalizedRegion} for industry: ${industryName} (ID: ${selectedIndustry?.id || 'N/A'})`);

        updated.subIndustry = '';
        updated.subIndustryId = null;
        updated.industry = '';
        // Sub-industries will be loaded by useEffect
      }

      if (field === 'subIndustry') {
        // Don't override industry with subIndustry - keep majorIndustry as the main industry
        // The subIndustry is just for filtering/display purposes
        updated.subIndustry = value;
      }

      return updated;
    });
  };

  const handleResponseChange = (questionId, optionIndex) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < emhaQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResults();
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = async () => {
    setLoading(true);
    setError(null);

    const categoryScores = {};
    categories.forEach(category => {
      const categoryQuestions = emhaQuestions.filter(q => q.category === category);
      const categoryTotal = categoryQuestions.reduce((sum, q) => {
        return sum + (responses[q.id] || 0);
      }, 0);
      categoryScores[category] = categoryTotal / categoryQuestions.length;
    });

    const overallScore = Object.values(categoryScores).reduce((sum, score) => sum + score, 0) / categories.length;
    const leapPhase = leapPhases.find(phase => overallScore >= phase.range[0] && overallScore < phase.range[1]) || leapPhases[4];

    try {
      // Ensure we send the correct industry (majorIndustry) and region
      const assessmentData = {
        type: 'EMHA',
        facility_info: {
          ...facilityInfo,
          // Use majorIndustry as the industry name, not subIndustry
          industry: facilityInfo.majorIndustry || facilityInfo.industry,
          // Ensure region is included
          region: facilityInfo.region || 'naics'
        },
        responses: responses,
        user_id: user?.id || null,
        scores: {
          overall: overallScore,
          categories: categoryScores,
          leap_phase: leapPhase.phase
        },
        completed_at: new Date().toISOString()
      };
      
      logger.log('Submitting EMHA assessment with data:', {
        industry: assessmentData.facility_info.industry,
        region: assessmentData.facility_info.region,
        majorIndustry: facilityInfo.majorIndustry,
        subIndustry: facilityInfo.subIndustry
      });

      const response = await apiClient.post('/emha/submit', assessmentData);
      navigate(`/emha/results/${response.data.assessment_id}`);

    } catch (error) {
      logger.error('Error submitting EMHA assessment:', error);
      logger.error('Error response:', error.response);
      setLoading(false);

      // Handle 409 Conflict (duplicate assessment) and 400 Bad Request
      if (error.response?.status === 409 || error.response?.status === 400) {
        const errorData = error.response?.data;
        let detail = errorData?.detail;

        // If detail is not available, check message field (some error wrappers put it there)
        if (!detail && errorData?.message) {
          detail = errorData.message;
        }

        // Parse detail if it's a string containing Python dict or JSON
        if (typeof detail === 'string') {
          try {
            // Check if it's a Python dict format (single quotes) or JSON (double quotes)
            if (detail.includes('error') && (detail.includes("'") || detail.includes('"'))) {
              // Use a more sophisticated approach: parse Python dict manually
              // This handles values with quotes inside them (like "You've")
              const parsePythonDict = (str) => {
                const result = {};
                // Remove outer braces and whitespace
                let content = str.trim();
                if (content.startsWith('{')) content = content.slice(1);
                if (content.endsWith('}')) content = content.slice(0, -1);
                content = content.trim();
                
                // Split by commas, but be careful with commas inside quoted strings
                const fields = [];
                let current = '';
                let inQuotes = false;
                let quoteChar = null;
                let depth = 0;
                
                for (let i = 0; i < content.length; i++) {
                  const char = content[i];
                  const prevChar = i > 0 ? content[i - 1] : '';
                  
                  // Check for quote start/end (not escaped)
                  if ((char === "'" || char === '"') && prevChar !== '\\') {
                    if (!inQuotes) {
                      inQuotes = true;
                      quoteChar = char;
                    } else if (char === quoteChar) {
                      inQuotes = false;
                      quoteChar = null;
                    }
                  } else if (char === '{' && !inQuotes) {
                    depth++;
                  } else if (char === '}' && !inQuotes) {
                    depth--;
                  } else if (char === ',' && !inQuotes && depth === 0) {
                    fields.push(current.trim());
                    current = '';
                    continue;
                  }
                  current += char;
                }
                if (current.trim()) {
                  fields.push(current.trim());
                }
                
                // Parse each field
                fields.forEach(field => {
                  const colonIndex = field.indexOf(':');
                  if (colonIndex === -1) return;
                  
                  let key = field.substring(0, colonIndex).trim();
                  let value = field.substring(colonIndex + 1).trim();
                  
                  // Remove quotes from key
                  key = key.replace(/^['"]|['"]$/g, '');
                  
                  // Parse value - handle both single and double quoted strings
                  if ((value.startsWith("'") && value.endsWith("'")) || 
                      (value.startsWith('"') && value.endsWith('"'))) {
                    value = value.slice(1, -1);
                  } else if (value === 'True') {
                    value = true;
                  } else if (value === 'False') {
                    value = false;
                  } else if (value === 'None') {
                    value = null;
                  } else if (!isNaN(value) && value !== '') {
                    value = Number(value);
                  }
                  
                  result[key] = value;
                });
                
                return result;
              };
              
              detail = parsePythonDict(detail);
              logger.log('Parsed detail:', detail);
            }
          } catch (e) {
            // If parsing fails, try to extract fields manually with better regex
            logger.warn('Could not parse error detail, trying regex extraction:', e);
            logger.warn('Original detail string:', detail);
            
            // Extract fields using a more sophisticated approach that handles mixed quotes
            const extractField = (fieldName) => {
              // Try multiple patterns to handle different quote combinations
              const patterns = [
                // Key in single quotes, value in double quotes (most common for messages with quotes)
                new RegExp(`'${fieldName}'\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`),
                // Key in single quotes, value in single quotes
                new RegExp(`'${fieldName}'\\s*:\\s*'((?:[^'\\\\]|\\\\.)*)'`),
                // Key in double quotes, value in double quotes
                new RegExp(`"${fieldName}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`),
                // Key in double quotes, value in single quotes
                new RegExp(`"${fieldName}"\\s*:\\s*'((?:[^'\\\\]|\\\\.)*)'`),
              ];
              
              for (const pattern of patterns) {
                const match = detail.match(pattern);
                if (match && match[1]) {
                  return match[1].replace(/\\'/g, "'").replace(/\\"/g, '"');
                }
              }
              return null;
            };
            
            const message = extractField('message');
            const error = extractField('error');
            const id = extractField('existing_assessment_id');
            
            if (message || error) {
              detail = {
                message: message || 'Duplicate assessment detected',
                error: error || 'duplicate_assessment',
                existing_assessment_id: id || null
              };
              logger.log('Extracted detail from regex:', detail);
            }
          }
        }

        // If detail is still a string after parsing attempts, try one more extraction
        if (typeof detail === 'string' && detail.includes('error')) {
          logger.warn('Detail is still a string after parsing, attempting final extraction');
          // Try more specific regex patterns that handle mixed quotes
          const extractMessage = () => {
            // Try: 'message': "value" (single quote key, double quote value)
            let match = detail.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/);
            if (match) return match[1];
            // Try: 'message': 'value' (both single quotes)
            match = detail.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/);
            if (match) return match[1];
            // Try: "message": "value" (both double quotes)
            match = detail.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            if (match) return match[1];
            return null;
          };
          
          const extractError = () => {
            const patterns = [
              /'error'\s*:\s*"([^"]+)"/,
              /'error'\s*:\s*'([^']+)'/,
              /"error"\s*:\s*"([^"]+)"/,
            ];
            for (const pattern of patterns) {
              const match = detail.match(pattern);
              if (match) return match[1];
            }
            return null;
          };
          
          const extractId = () => {
            const patterns = [
              /'existing_assessment_id'\s*:\s*"([^"]+)"/,
              /'existing_assessment_id'\s*:\s*'([^']+)'/,
              /"existing_assessment_id"\s*:\s*"([^"]+)"/,
            ];
            for (const pattern of patterns) {
              const match = detail.match(pattern);
              if (match) return match[1];
            }
            return null;
          };
          
          const message = extractMessage();
          const error = extractError();
          const id = extractId();
          
          if (message || error) {
            detail = {
              message: message || 'Duplicate assessment detected',
              error: error || 'duplicate_assessment',
              existing_assessment_id: id || null
            };
            logger.log('Final extraction result:', detail);
          }
        }

        // Handle duplicate assessment error
        if (detail && (typeof detail === 'object') && (detail.error === 'duplicate_assessment' || detail.error === 'Duplicate assessment' || detail.error === 'Duplicate Assessment')) {
          const errorMessage = detail.message || 'You have already submitted this assessment. Please view your existing results instead.';
          const existingAssessmentId = detail.existing_assessment_id;
          const existingIndustry = detail.existing_industry || facilityInfo.majorIndustry || facilityInfo.industry || 'this industry';
          
          setError(
            <div className="space-y-3">
              <div className="font-semibold text-red-800 dark:text-red-200">{errorMessage}</div>
              <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded">
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>What you can do:</strong>
                </div>
                <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-none">
                  <li>• View your existing {existingIndustry} EMHA assessment in the <Link to="/dashboard" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">Dashboard</Link></li>
                  {existingAssessmentId && (
                    <li>• <Link to={`/emha/results/${existingAssessmentId}`} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">View your existing assessment results</Link></li>
                  )}
                  <li>• Complete an EMHA assessment for a different industry</li>
                  <li>• Contact support if you need to update your existing assessment</li>
                </ul>
              </div>
            </div>
          );
        } else if (detail && typeof detail === 'object' && detail.message) {
          setError(detail.message);
        } else if (typeof detail === 'string') {
          // Last resort: try to extract just the message from the string
          // Handle: 'message': "value" format
          let messageMatch = detail.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/);
          if (!messageMatch) {
            messageMatch = detail.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/);
          }
          if (!messageMatch) {
            messageMatch = detail.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
          }
          
          if (messageMatch && messageMatch[1]) {
            setError(messageMatch[1]);
          } else {
            // If we can't extract, show a generic message instead of raw string
            setError('You have already submitted this assessment. Please view your existing results in the Dashboard.');
          }
        } else if (errorData?.message) {
          setError(errorData.message);
        } else {
          setError('Failed to submit assessment. Please try again.');
        }
      } else {
        // For other errors, try to extract the message
        const errorMessage = error.response?.data?.detail?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.detail ||
                           error.message ||
                           'Failed to submit assessment. Please check your connection and try again.';
        setError(errorMessage);
      }

      setStep(2);
    }
  };

  const progress = ((currentQuestion + 1) / emhaQuestions.length) * 100;
  const currentQ = emhaQuestions[currentQuestion];

  // Loading state
  if (dataLoading) {
    return <DataLoadingState />;
  }

  // Error state
  if (dataError) {
    return <ErrorState message={dataError} />;
  }

  // No questions validation
  if (!emhaQuestions || emhaQuestions.length === 0) {
    return <NoQuestionsState />;
  }

  // Step 1: Facility Information
  if (step === 1) {
    return (
      <>
        <GuidanceModal
          visible={showGuidanceModal}
          userHasChosen={userHasChosen}
          requestingConsultant={requestingConsultant}
          onClose={() => {
            navigate('/dashboard');
          }}
          onSelectSelfGuided={() => {
            setShowGuidanceModal(false);
            setUserHasChosen(true);
          }}
          onRequestConsultant={handleRequestConsultantGuidance}
        />

        <FacilityInfoStep
          facilityInfo={facilityInfo}
          industries={industries}
          availableSubIndustries={availableSubIndustries}
          emhaQuestionCount={emhaQuestions.length}
          onChangeField={handleFacilityInfoChange}
          onOpenGuidanceModal={() => {
            setShowGuidanceModal(true);
            // Allow modal to show even if user has chosen before
            // This allows reopening the modal if needed
          }}
          onStartAssessment={() => setStep(2)}
        />
      </>
    );
  }

  // Step 2: Assessment Questions
  if (step === 2) {
    return (
      <QuestionsStep
        error={error}
        progress={progress}
        currentIndex={currentQuestion}
        totalQuestions={emhaQuestions.length}
        currentQuestion={currentQ}
        responses={responses}
        onResponseChange={handleResponseChange}
        onPrevious={previousQuestion}
        onNext={nextQuestion}
        isLoading={loading}
      />
    );
  }

  // Step 3: Processing/Results
  return <ProcessingStep loading={loading} />;
};

export default EMHAAssessment;